import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import {
  diaMes,
  diasDoPeriodo,
  gravarCelulas,
  lerCalendarioDeFerias,
  lerFerias,
} from '../../escala';
import type { FeriasDaPessoaNaEscala } from '../../escala';
import {
  LancarFerias,
  corDaPessoa,
  blocos,
  dataBr,
  diasEntre,
  mesesDoPeriodo,
  umAnoDepois,
} from './CalendarioDeFerias';
import type { Bloco, Editando } from './CalendarioDeFerias';

/**
 * CALENDÁRIO ANUAL DE FÉRIAS — "no formato de calendário mesmo… e dentro do calendário pôr o nome
 * do funcionário" (dono 24/10/2026). Os meses do ano fechado (24/10/2026 a 24/10/2027) como um
 * calendário de parede: cada dia mostra quem está de férias (nome + cor da pessoa).
 *
 * Lançar: escolhe a pessoa, toca no 1º dia e no último — grava FE no quadro da loja.
 */

const SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const NOMES_DOS_MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
const MAX_NOMES = 3;
const primeiroNome = (n: string) => n.split(' ')[0] ?? n;
const dow = (d: string) => new Date(`${d}T12:00:00Z`).getUTCDay();

export function CalendarioAnual({ sessao, aoMudar }: { sessao: Sessao; aoMudar: () => void }) {
  const [de, setDe] = useState<string | null>(null);
  const [cal, setCal] = useState<Awaited<ReturnType<typeof lerCalendarioDeFerias>> | null>(null);
  const [pessoas, setPessoas] = useState<FeriasDaPessoaNaEscala[] | null>(null);
  const [escolhida, setEscolhida] = useState<string>('');
  const [inicio, setInicio] = useState<string | null>(null);
  const [editando, setEditando] = useState<Editando | null>(null);
  const [diaAberto, setDiaAberto] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);

  const carregar = useCallback(() => {
    void lerCalendarioDeFerias(de ?? undefined, de ? umAnoDepois(de) : undefined).then(setCal);
    void lerFerias().then((r) => r.ok && setPessoas(r.dados.pessoas));
  }, [de]);
  useEffect(carregar, [carregar]);

  const dados = cal?.ok ? cal.dados : null;
  const meses = useMemo(() => (dados ? mesesDoPeriodo(dados.de, dados.ate) : []), [dados]);
  const cor = useMemo(
    () => new Map((pessoas ?? []).map((p, i) => [p.pessoaId, corDaPessoa(i)])),
    [pessoas],
  );
  const nome = useMemo(() => new Map((pessoas ?? []).map((p) => [p.pessoaId, p.nome])), [pessoas]);
  // dia → quem está de férias (na ordem do quadro)
  const porDia = useMemo(() => {
    const m = new Map<string, string[]>();
    const ordem = new Map((pessoas ?? []).map((p, i) => [p.pessoaId, i]));
    for (const f of dados?.ferias ?? []) m.set(f.dia, [...(m.get(f.dia) ?? []), f.pessoaId]);
    for (const v of m.values()) v.sort((a, b) => (ordem.get(a) ?? 0) - (ordem.get(b) ?? 0));
    return m;
  }, [dados, pessoas]);
  const blocosDa = useMemo(() => {
    const m = new Map<string, Bloco[]>();
    const d = new Map<string, string[]>();
    for (const f of dados?.ferias ?? []) d.set(f.pessoaId, [...(d.get(f.pessoaId) ?? []), f.dia]);
    for (const [k, v] of d) m.set(k, blocos(v));
    return m;
  }, [dados]);
  const feriados = useMemo(
    () => new Map((dados?.feriados ?? []).map((f) => [f.dia, f.nome])),
    [dados],
  );

  if (cal && !cal.ok) return <ErroDeArea oQue="o calendário de férias" aoTentar={carregar} />;
  if (!dados || !pessoas) return <div className="qh-carregando">Montando o calendário…</div>;

  const pessoa = pessoas.find((p) => p.pessoaId === escolhida) ?? null;

  const tocar = (dia: string) => {
    if (dia < dados.de || dia > dados.ate) return;
    if (!pessoa) return setDiaAberto(dia);
    // tocou num dia de férias da pessoa: abre aquele bloco para mudar/desfazer
    const bl = (blocosDa.get(pessoa.pessoaId) ?? []).find((b) => dia >= b.de && dia <= b.ate);
    if (bl && !inicio) return setEditando({ pessoa, bloco: bl, sugestao: bl.de });
    if (!inicio) return setInicio(dia);
    const [a, b] = inicio <= dia ? [inicio, dia] : [dia, inicio];
    setInicio(null);
    setEditando({ pessoa, bloco: null, sugestao: a, dias: diasEntre(a, b) + 1 });
  };

  const andar = (n: number) => {
    const base = new Date(`${dados.de}T12:00:00Z`);
    base.setUTCMonth(base.getUTCMonth() + n);
    setDe(base.toISOString().slice(0, 10));
  };

  return (
    <div className="qh-corpo">
      <div className="qh-barra">
        <div className="qh-periodo">
          <button
            type="button"
            className="qh-btn-icone"
            onClick={() => andar(-1)}
            aria-label="Um mês para trás"
          >
            <Icone nome="arrow-l" tamanho={16} />
          </button>
          <span className="qh-periodo-txt">
            {dataBr(dados.de)} a {dataBr(dados.ate)}
          </span>
          <button
            type="button"
            className="qh-btn-icone"
            onClick={() => andar(1)}
            aria-label="Um mês para frente"
          >
            <Icone nome="arrow" tamanho={16} />
          </button>
          {de && (
            <button
              type="button"
              className="qh-btn qh-btn-sec qh-btn-curto"
              onClick={() => setDe(null)}
            >
              Hoje
            </button>
          )}
        </div>
        <label className="qh-campo-inline qh-quem">
          Lançar férias de
          <select
            value={escolhida}
            onChange={(e) => {
              setEscolhida(e.target.value);
              setInicio(null);
            }}
          >
            <option value="">— escolha a pessoa —</option>
            {pessoas.map((p) => (
              <option key={p.pessoaId} value={p.pessoaId}>
                {p.nome}
                {p.ferias ? ` · saldo ${String(p.ferias.saldo)}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className={`qh-instrucao${pessoa ? ' qh-instrucao-on' : ''}`} role="status">
        {pessoa ? (
          <>
            <span className="qh-cor" style={{ background: cor.get(pessoa.pessoaId) }} />
            <span>
              <strong>{pessoa.nome}</strong>
              {pessoa.ferias
                ? ` · saldo ${String(pessoa.ferias.saldo)} dias${pessoa.ferias.proximoVencimento ? ` · tirar até ${dataBr(pessoa.ferias.proximoVencimento.limite)}` : ''}`
                : ' · sem data de admissão'}
              {' — '}
              {inicio
                ? `começa em ${diaMes(inicio)}: agora toque no ÚLTIMO dia das férias.`
                : 'toque no PRIMEIRO dia das férias (ou numa férias dela para mudar).'}
            </span>
            {inicio && (
              <button
                type="button"
                className="qh-btn qh-btn-sec qh-btn-curto"
                onClick={() => setInicio(null)}
              >
                Recomeçar
              </button>
            )}
          </>
        ) : (
          'Escolha a pessoa acima para lançar férias. Toque num dia para ver quem está de férias.'
        )}
      </div>

      {aviso && (
        <div
          className={`qh-aviso qh-aviso-${aviso.tom}`}
          role={aviso.tom === 'erro' ? 'alert' : 'status'}
        >
          {aviso.texto}
          <button type="button" onClick={() => setAviso(null)} aria-label="Fechar aviso">
            <Icone nome="x" tamanho={14} />
          </button>
        </div>
      )}

      {blocosDa.size > 0 && (
        <div className="qh-legenda-cores" aria-label="Quem tem férias no período">
          {pessoas
            .filter((p) => blocosDa.has(p.pessoaId))
            .map((p) => {
              const total = (blocosDa.get(p.pessoaId) ?? []).reduce((s, b) => s + b.dias, 0);
              return (
                <button
                  key={p.pessoaId}
                  type="button"
                  className={`qh-chip-pessoa${escolhida === p.pessoaId ? ' qh-chip-on' : ''}`}
                  onClick={() => {
                    setEscolhida(escolhida === p.pessoaId ? '' : p.pessoaId);
                    setInicio(null);
                  }}
                  title={`${p.nome}: ${String(total)} dias de férias no período`}
                >
                  <span className="qh-cor" style={{ background: cor.get(p.pessoaId) }} />
                  {p.nome}
                  <small>{total}d</small>
                </button>
              );
            })}
        </div>
      )}

      <div className="qh-meses">
        {meses.map((m) => {
          const dias = diasDoPeriodo(m.de, m.ate);
          const vazios = dow(m.de);
          return (
            <section
              key={m.de}
              className="qh-mes"
              aria-label={`${NOMES_DOS_MESES[m.mes - 1] ?? ''} ${String(m.ano)}`}
            >
              <h3>
                {NOMES_DOS_MESES[m.mes - 1]} <small>{m.ano}</small>
              </h3>
              <div className="qh-mes-grade">
                {SEMANA.map((s, i) => (
                  <span
                    key={`${s}${String(i)}`}
                    className={`qh-mes-sem${i === 0 ? ' qh-mes-dom' : ''}`}
                  >
                    {s}
                  </span>
                ))}
                {Array.from({ length: vazios }, (_, i) => (
                  <span key={`v${String(i)}`} className="qh-mes-vazio" />
                ))}
                {dias.map((d) => {
                  const quem = porDia.get(d) ?? [];
                  const fora = d < dados.de || d > dados.ate;
                  const minha = pessoa ? quem.includes(pessoa.pessoaId) : false;
                  const naSelecao = pessoa && inicio && d === inicio;
                  const ordenados =
                    pessoa && minha
                      ? [pessoa.pessoaId, ...quem.filter((x) => x !== pessoa.pessoaId)]
                      : quem;
                  const cls = [
                    'qh-mes-dia',
                    dow(d) === 0 ? 'qh-mes-dom' : '',
                    feriados.has(d) ? 'qh-mes-feriado' : '',
                    d === dados.hoje ? 'qh-mes-hoje' : '',
                    fora ? 'qh-mes-fora' : '',
                    quem.length ? 'qh-mes-com' : '',
                    minha ? 'qh-mes-minha' : '',
                    naSelecao ? 'qh-mes-inicio' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  const titulo = [
                    dataBr(d),
                    feriados.get(d) ? `Feriado: ${feriados.get(d) ?? ''}` : '',
                    quem.length
                      ? `De férias: ${quem.map((x) => nome.get(x) ?? '').join(', ')}`
                      : 'Ninguém de férias',
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <button
                      key={d}
                      type="button"
                      className={cls}
                      onClick={() => tocar(d)}
                      disabled={fora}
                      title={titulo}
                      aria-label={titulo}
                    >
                      <span className="qh-mes-num">{Number(d.slice(8))}</span>
                      {ordenados.slice(0, MAX_NOMES).map((x) => (
                        <span
                          key={x}
                          className={`qh-mes-nome${pessoa && x !== pessoa.pessoaId ? ' qh-mes-nome-fraco' : ''}`}
                          style={{ background: cor.get(x) }}
                        >
                          {primeiroNome(nome.get(x) ?? '')}
                        </span>
                      ))}
                      {ordenados.length > MAX_NOMES && (
                        <span className="qh-mes-mais">+{ordenados.length - MAX_NOMES}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {diaAberto && (
        <div className="qh-modal-fundo" onClick={() => setDiaAberto(null)} role="presentation">
          <div
            className="qh-modal"
            role="dialog"
            aria-modal="true"
            aria-label={dataBr(diaAberto)}
            onClick={(e) => e.stopPropagation()}
          >
            <header>
              <div>
                <h2>{dataBr(diaAberto)}</h2>
                {feriados.get(diaAberto) && <p>Feriado: {feriados.get(diaAberto)}</p>}
              </div>
              <button
                type="button"
                className="qh-btn-icone"
                onClick={() => setDiaAberto(null)}
                aria-label="Fechar"
              >
                <Icone nome="x" tamanho={16} />
              </button>
            </header>
            {(porDia.get(diaAberto) ?? []).length === 0 ? (
              <p className="qh-mudo">Ninguém de férias neste dia.</p>
            ) : (
              <ul className="qh-quem-lista">
                {(porDia.get(diaAberto) ?? []).map((x) => {
                  const p = pessoas.find((y) => y.pessoaId === x);
                  const bl = (blocosDa.get(x) ?? []).find(
                    (b) => diaAberto >= b.de && diaAberto <= b.ate,
                  );
                  return (
                    <li key={x}>
                      <span className="qh-cor" style={{ background: cor.get(x) }} />
                      <span>
                        <strong>{nome.get(x)}</strong>
                        {bl && (
                          <small>
                            {' '}
                            · {diaMes(bl.de)} a {diaMes(bl.ate)} ({bl.dias} dias)
                          </small>
                        )}
                      </span>
                      {p && bl && (
                        <button
                          type="button"
                          className="qh-btn qh-btn-sec qh-btn-curto"
                          onClick={() => {
                            setDiaAberto(null);
                            setEditando({ pessoa: p, bloco: bl, sugestao: bl.de });
                          }}
                        >
                          Mudar
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {editando && (
        <LancarFerias
          e={editando}
          outras={dados.outras.filter((o) => o.pessoaId === editando.pessoa.pessoaId)}
          aoFechar={() => setEditando(null)}
          aoGravar={async (mudancas, texto) => {
            const r = await gravarCelulas(sessao, mudancas);
            setEditando(null);
            if (r.tipo === 'confirmada') {
              setAviso({ tom: 'ok', texto });
              carregar();
              aoMudar();
            } else
              setAviso({
                tom: 'erro',
                texto: r.tipo === 'recusada' ? r.mensagem : 'A internet caiu no meio — confira.',
              });
          }}
        />
      )}
    </div>
  );
}
